import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import Pagination from '../components/Pagination';
import { formatJoinedDate } from '../api/agents';
import { AppUserRow, listUsers } from '../api/users';
import { extractApiError } from '../api/legalServices';

const PAGE_LIMIT = 20;

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AppUserRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Debounce search input -> committed search by 350ms
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listUsers({
        search: search || undefined,
        page,
        limit: PAGE_LIMIT,
      });
      setUsers(res.users);
      setTotal(res.total);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (u: AppUserRow) => navigate(`/admin/users/${u.id}`);

  return (
    <div>
      {/* Search + filter row */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="relative flex-1" style={{ maxWidth: 420 }}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2">
            <Icon name="search" size={18} color="var(--g400)" />
          </span>
          <input
            className="form-input"
            style={{ paddingLeft: 40 }}
            placeholder="Search users by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <span className="text-[13px] text-g500">
          {loading ? 'Loading…' : `${total} user${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{ background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(220,38,38,.12)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load users</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      <div
        className="bg-white rounded-card overflow-x-auto border border-g100"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['User', 'Email', 'Applications', 'Orders', 'Inquiries', 'Joined', ''].map((h, i) => (
                <th
                  key={i}
                  className={
                    'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                    (i === 6 ? ' text-right' : '')
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <div className="py-12 text-center text-g500">
                    <Icon name="user" size={28} color="var(--g400)" /> Loading users...
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="rounded-[20px] flex items-center justify-center mb-5"
                      style={{ width: 72, height: 72, background: 'var(--g100)' }}
                    >
                      <Icon name="user" size={32} color="var(--g400)" />
                    </div>
                    <div className="text-[17px] font-semibold text-ink mb-1.5">No users found</div>
                    <div className="text-sm text-g500" style={{ maxWidth: 340 }}>
                      {search
                        ? 'Try a different name or email.'
                        : 'No one has signed up in the app yet.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((u) => {
                return (
                  <tr
                    key={u.id}
                    className="hover:bg-g50 transition-colors cursor-pointer"
                    onClick={() => open(u)}
                  >
                    <td className="px-4 py-3.5 whitespace-nowrap border-b border-g100">
                      <div className="flex items-center gap-3">
                        <Avatar src={u.avatar} name={u.name || u.email} id={u.id} size={36} />
                        <div>
                          <div className="font-semibold text-sm">
                            {u.name || <span className="text-g400 italic">Unnamed</span>}
                          </div>
                          <div className="text-xs text-g500">ID #{u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className="flex items-center gap-2">
                        <Icon name="mail" size={14} color="var(--g400)" />
                        {u.email}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g700 whitespace-nowrap border-b border-g100">
                      {u.counts.applications}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g700 whitespace-nowrap border-b border-g100">
                      {u.counts.orders}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g700 whitespace-nowrap border-b border-g100">
                      {u.counts.inquiries}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {formatJoinedDate(u.createdAt)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100 text-right">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          open(u);
                        }}
                      >
                        <Icon name="eye" size={14} /> View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={PAGE_LIMIT} total={total} onChange={setPage} disabled={loading} />
    </div>
  );
};

export default Users;
